import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import de from './locales/de/shiftbook.json';
import en from './locales/en/shiftbook.json';
import fr from './locales/fr/shiftbook.json';

const LOCALE_COOKIE = 'locale';
const DEFAULT_LANGUAGE: SupportedLanguage = 'de';

export const SUPPORTED_LANGUAGES = ['de', 'en', 'fr'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Map i18next language key to BCP 47 cookie value */
export const LOCALE_MAP: Record<SupportedLanguage, string> = {
  de: 'de-DE',
  en: 'en-US',
  fr: 'fr-CH',
};

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  de: 'DE',
  en: 'EN',
  fr: 'FR',
};

export function normalizeLanguage(lang: string | undefined | null): SupportedLanguage {
  if (!lang) {
    return DEFAULT_LANGUAGE;
  }

  const normalized = lang.toLowerCase().split('-')[0];
  if (SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)) {
    return normalized as SupportedLanguage;
  }

  return DEFAULT_LANGUAGE;
}

export function getLanguageLabel(lang: string | undefined | null): string {
  return LANGUAGE_LABELS[normalizeLanguage(lang)];
}

export function getNextLanguage(lang: string | undefined | null): SupportedLanguage {
  const current = normalizeLanguage(lang);
  const index = SUPPORTED_LANGUAGES.indexOf(current);
  return SUPPORTED_LANGUAGES[(index + 1) % SUPPORTED_LANGUAGES.length];
}

export function getInitialLanguage(): SupportedLanguage {
  // 1. Check locale cookie
  try {
    const cookie = document.cookie.split('; ').find((c) => c.startsWith(`${LOCALE_COOKIE}=`));
    if (cookie) {
      const value = cookie.split('=')[1];
      if (value === 'de-DE') return 'de';
      if (value === 'en-US') return 'en';
      if (value === 'fr-CH') return 'fr';
      return normalizeLanguage(value);
    }
  } catch {
    // cookie access failed
  }

  // 2. Fall back to German when no locale cookie is present.
  return DEFAULT_LANGUAGE;
}

export function setLocaleCookie(lang: string) {
  const bcp47 = LOCALE_MAP[normalizeLanguage(lang)];
  document.cookie = `${LOCALE_COOKIE}=${bcp47};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
}

const resources = {
  de: { shiftbook: de },
  en: { shiftbook: en },
  fr: { shiftbook: fr },
};

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: 'de',
  defaultNS: 'shiftbook',
  ns: ['shiftbook'],
  interpolation: {
    escapeValue: false, // React handles XSS
  },
});

// Set cookie on initial load
setLocaleCookie(i18n.language);

// Update cookie whenever language changes
i18n.on('languageChanged', (lang) => {
  setLocaleCookie(lang);
});

export default i18n;
